/** Query definitions shared by pages and prefetchers, so a hover or an arrow key can warm the cache. */
import { queryOptions } from '@tanstack/react-query';
import { rpc } from './rpc';

export const accountQuery = (accountId: string) =>
  queryOptions({
    queryKey: ['accounts.get', accountId],
    queryFn: () => rpc.accounts.get({ accountId }),
    staleTime: 30_000,
  });

/** The ticket Dialpad's recap drafts for a call; the caller polls it while the recap is on its way. */
export const callDraftQuery = (rootCallId: string) =>
  queryOptions({
    queryKey: ['calls.draft', rootCallId],
    queryFn: () => rpc.calls.draft({ rootCallId }),
    staleTime: 0,
  });

/** Dialpad's transcript of a call (redacted); fetched when someone opens it. */
export const callTranscriptQuery = (rootCallId: string) =>
  queryOptions({
    queryKey: ['calls.transcript', rootCallId],
    queryFn: () => rpc.calls.transcript({ rootCallId }),
    staleTime: 60_000,
  });

/** The answered call a ticket for this account (or on this number) is probably about, if one just ended. */
export const recentCallQuery = (input: { accountId?: string; phone?: string }) =>
  queryOptions({
    queryKey: ['calls.recent', input.accountId ?? '', input.phone ?? ''],
    queryFn: () => rpc.calls.recent(input),
    staleTime: 30_000,
  });

export const searchQuery = (q: string, includeInactive: boolean) =>
  queryOptions({
    queryKey: ['accounts.search', q, includeInactive],
    queryFn: () => rpc.accounts.search({ q, includeInactive }),
    staleTime: 15_000,
  });

export const resolveQuery = (phone: string) =>
  queryOptions({
    queryKey: ['accounts.resolve', phone],
    queryFn: () => rpc.accounts.resolve({ phone }),
    staleTime: 15_000,
  });

export const boardQuery = () =>
  queryOptions({
    queryKey: ['tickets.board'],
    queryFn: () => rpc.tickets.board({}),
    staleTime: 10_000,
  });

export const ticketQuery = (ticketId: string) =>
  queryOptions({
    queryKey: ['tickets.get', ticketId],
    queryFn: () => rpc.tickets.get({ ticketId }),
    staleTime: 30_000,
  });

export const draftsQuery = (limit = 50, cursor?: string) =>
  queryOptions({
    queryKey: ['drafts.list', limit, cursor ?? null],
    queryFn: () => rpc.drafts.list({ limit, ...(cursor ? { cursor } : {}) }),
    staleTime: 15_000,
  });

export const staffQuery = () =>
  queryOptions({
    queryKey: ['staff.list'],
    queryFn: () => rpc.staff.list({}),
    staleTime: 15_000,
  });

export const integrationsQuery = () =>
  queryOptions({
    queryKey: ['integrations.health'],
    queryFn: () => rpc.integrations.health({}),
    staleTime: 30_000,
  });

/** Where links into Slack point; fixed for the life of the API, so fetched once. */
export const slackLinksQuery = () =>
  queryOptions({
    queryKey: ['integrations.slackLinks'],
    queryFn: () => rpc.integrations.slackLinks({}),
    staleTime: Infinity,
  });

/** Slack profile photos; they change rarely, so an hour is fresh enough. */
export const avatarsQuery = () =>
  queryOptions({
    queryKey: ['staff.avatars'],
    queryFn: () => rpc.staff.avatars({}),
    staleTime: 60 * 60_000,
  });

export const mentionableQuery = () =>
  queryOptions({
    queryKey: ['staff.mentionable'],
    queryFn: () => rpc.staff.mentionable({}),
    staleTime: 5 * 60_000,
  });

export const notificationsQuery = () =>
  queryOptions({
    queryKey: ['notifications.list'],
    queryFn: () => rpc.notifications.list({ limit: 30 }),
    staleTime: 30_000,
  });

/** The ticket pickers (categories, products). Prefetched at sign-in so quick capture opens full; invalidated after a Settings edit. */
/** The Overview page: fresh for 30 s, re-read every minute, and after any ticket change (live-refetch). */
export const overviewQuery = () =>
  queryOptions({
    queryKey: ['overview.get'],
    queryFn: () => rpc.overview.get({}),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

export const vocabQuery = () =>
  queryOptions({
    queryKey: ['vocab.list'],
    queryFn: () => rpc.vocab.list({}),
    staleTime: 5 * 60_000,
  });
